import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { X, Send, Sparkles } from "lucide-react";
import { API, LOGO } from "../lib/constants";

const linkify = (text) => {
  const parts = text.split(/(https?:\/\/[^\s)]+)/g);
  return parts.map((p, i) => {
    if (!/^https?:\/\//.test(p)) return p;
    const internal = p.replace(/^https?:\/\/(www\.)?fiscoconseils\.com/, "");
    const isInternal = internal !== p;
    return <a key={i} href={isInternal ? internal : p} target={isInternal ? "_self" : "_blank"} rel="noopener noreferrer" data-testid="chat-guide-link" className="underline underline-offset-4 font-semibold break-all">{isInternal ? internal : p}</a>;
  });
};

const WELCOME = "Bonjour, je suis le conseiller fiscal IA de Fiscoconseils. Je peux vous aider à comprendre votre situation fiscale et vous orienter vers le bon accompagnement. Êtes-vous frontalier, résident suisse, indépendant ou représentant d'une entreprise ?";

export const ChatWidget = ({ open, onClose }) => {
  const [messages, setMessages] = useState([{ id: "welcome", role: "assistant", content: WELCOME }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => {
    const existing = sessionStorage.getItem("fisco_chat_session");
    if (existing) return existing;
    const id = `web-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    sessionStorage.setItem("fisco_chat_session", id);
    return id;
  });
  const bottomRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setLoading(true);
    setMessages((m) => [...m, { id: crypto.randomUUID(), role: "user", content: text }, { id: crypto.randomUUID(), role: "assistant", content: "" }]);

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const data = JSON.parse(part.slice(6));
          if (data.delta) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + data.delta };
              return copy;
            });
          } else if (data.error) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], content: data.error };
              return copy;
            });
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { ...copy[copy.length - 1], content: "Une erreur est survenue. Veuillez réessayer dans un instant." };
        return copy;
      });
    }
    setLoading(false);
  };

  if (!open) return null;

  return (
    <div data-testid="chat-widget" className="fixed bottom-4 right-4 z-[70] w-[calc(100vw-2rem)] sm:w-[400px] h-[600px] max-h-[calc(100vh-2rem)] bg-white rounded-2xl shadow-[0_16px_48px_rgba(10,17,40,0.25)] border border-[#C4C8CC]/50 flex flex-col overflow-hidden">
      <div className="bg-[#0A1128] px-5 py-4 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <img src={LOGO} alt="" className="h-9 w-9 rounded-full bg-white p-0.5" />
          <div>
            <p className="text-white text-sm font-semibold">Conseiller fiscal IA</p>
            <p className="text-[#9CA3AF] text-xs">Fiscoconseils</p>
          </div>
        </div>
        <button data-testid="chat-close-btn" onClick={onClose} className="text-[#C4C8CC] hover:text-white transition-colors duration-200">
          <X size={20} />
        </button>
      </div>

      <div data-testid="chat-messages" className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#F8F9FA]">
        {messages.map((m, i) => (
          <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                m.role === "user" ? "bg-[#0A1128] text-white rounded-br-md" : "bg-white text-[#1F2937] border border-[#E5E7EB] rounded-bl-md"
              }`}
            >
              {m.content ? linkify(m.content) : (loading && i === messages.length - 1 ? "…" : "")}
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      <div className="px-4 py-2 bg-[#F8F9FA] shrink-0">
        <button
          data-testid="chat-devis-btn"
          onClick={() => { onClose(); navigate("/devis"); }}
          className="w-full text-xs font-semibold text-[#0A1128] border border-[#0A1128]/30 rounded-full py-2 flex items-center justify-center gap-2 hover:bg-white transition-colors duration-200"
        >
          <Sparkles size={13} strokeWidth={1.75} /> Demander un devis personnalisé
        </button>
      </div>

      <div className="border-t border-[#E5E7EB] p-3 flex gap-2 shrink-0 bg-white">
        <input
          data-testid="chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Posez votre question fiscale…"
          maxLength={2000}
          className="flex-1 text-sm px-4 py-2.5 rounded-full border border-[#C4C8CC]/60 focus:outline-none focus:border-[#0A1128] transition-colors duration-200"
        />
        <button
          data-testid="chat-send-btn"
          onClick={send}
          disabled={loading}
          className="bg-[#0A1128] text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-[#1A2342] transition-colors duration-200 disabled:opacity-50 shrink-0"
        >
          <Send size={16} />
        </button>
      </div>
    </div>
  );
};
